import { type ReactNode, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useToast } from '@/components/ui/toast';
import { DistributeModalContent } from './distribute-modal-content';

type Result = {
  total_redistributed: number;
  per_member: Record<string, number>;
};
type Role = 'approacher' | 'mago';

function useDistribute() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (role: Role) =>
      pb.send<Result>('/api/assignment/distribute', {
        method: 'POST',
        body: { role },
      }),
    onSuccess: (r) =>
      toast(`${r.total_redistributed} creators redistribuidos`, 'success'),
    onError: () => toast('Error al distribuir', 'error'),
  });
}

function roleLabel(role: Role): string {
  return role === 'approacher' ? 'Approachers' : 'Magos';
}

function TriggerButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): ReactNode {
  return (
    <Button variant="ghost" size="sm" onClick={onClick} className="gap-1">
      <RefreshCw size={14} /> Distribuir carga ({label})
    </Button>
  );
}

export function DistributeLoadButton({ role }: { role: Role }): ReactNode {
  const [open, setOpen] = useState(false);
  const distribute = useDistribute();
  const label = roleLabel(role);
  const close = (): void => setOpen(false);
  return (
    <>
      <TriggerButton label={label} onClick={() => setOpen(true)} />
      <Modal open={open} onClose={close} title="Confirmar distribución">
        <DistributeModalContent
          label={label}
          isPending={distribute.isPending}
          onConfirm={() => distribute.mutate(role, { onSuccess: close })}
          onCancel={close}
        />
      </Modal>
    </>
  );
}
